import React, { ChangeEvent, useEffect, useState } from "react";
import { Button, Col, Form, FormControl, Row, Table } from "react-bootstrap";
import { ICOIInfo } from "../../types/api";
import {
    addCOIExpiration,
    addCOIMessage,
    approveCOI,
    getCOI,
    rejectCOI,
} from "../../lib/api";
import { defaultDateFormat } from "../../lib/utils";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxHooks";
import { alertActions } from "../../features/alert/alertSlice";
import Select from "react-select";
import { Outlet, useLocation } from "react-router";
import { Link } from "react-router-dom";
import { UPLOADED_COI_URL } from "../../lib/globals";

export const DEFAULT_REJECT_MESSAGE =
    "Please list Baseball365 (PO Box 4, Janesville, WI 53547) as the certificate holder on your insurance. This is a requirement for play. Thank you.";

interface Filters {
    leagues: {
        options: Array<string>;
        filter: Array<string> | undefined;
    };
    ageLevels: {
        options: Array<string>;
        filter: Array<string> | undefined;
    };
    statuses: {
        options: ICOIInfo["status"][];
        filter: Array<string> | undefined;
    };
    uploadedOnly: {
        filter: number;
    };
    team: string | undefined;
    firstName: string | undefined;
    lastName: string | undefined;
    email: string | undefined;
}

const COIApproval = (): React.JSX.Element => {
    const dispatch = useAppDispatch();
    const [cois, setCois] = useState<ICOIInfo[]>();
    const [showMessage, setShowMessage] = useState<Record<number, boolean>>({});
    const [message, setMessage] = useState<Record<number, string>>({});
    const [showExpiration, setShowExpiration] = useState<
        Record<number, boolean>
    >({});
    const [expiration, setExpiration] = useState<Record<number, string>>({});

    const [filters, setFilters] = useState<Filters>({
        leagues: {
            options: [],
            filter: undefined,
        },
        ageLevels: {
            options: [],
            filter: undefined,
        },
        statuses: {
            options: ["Approved", "Rejected", "Pending"],
            filter: undefined,
        },
        uploadedOnly: {
            filter: 1,
        },
        team: undefined,
        firstName: undefined,
        lastName: undefined,
        email: undefined,
    });
    const location = useLocation();
    const user = useAppSelector(state => state.auth.user);
    const isOnApprovalPage = /coiApproval\/?$/.test(location.pathname);

    useEffect(() => {
        const getCOIs = async () => {
            await getCOI()
                .then(response => {
                    setCois(response.coi);

                    setFilters(prev => {
                        return {
                            ...prev,
                            leagues: {
                                ...prev.leagues,
                                options: response.coi.reduce(
                                    (acc: string[], c) => {
                                        c.league &&
                                            !acc.includes(c.league) &&
                                            acc.push(c.league);
                                        return acc;
                                    },
                                    [],
                                ),
                            },
                            ageLevels: {
                                ...prev.ageLevels,
                                options: response.coi.reduce(
                                    (acc: string[], c) => {
                                        !acc.includes(c.ageLevel) &&
                                            c.ageLevel !== null &&
                                            acc.push(c.ageLevel);
                                        return acc;
                                    },
                                    [],
                                ),
                            },
                            statuses: {
                                ...prev.statuses,
                                filter: ["Pending"],
                            },
                            uploadedOnly: {
                                ...prev.uploadedOnly,
                            },
                        };
                    });
                })
                .catch(e => {
                    dispatch(
                        alertActions.error({
                            title: "There was a problem retrieving the COIs",
                            text: "If the problem persists, please contact an admin.",
                            dismissible: true,
                        }),
                    );
                });
        };

        getCOIs();
    }, []);

    const handleApproval = async (teamId: ICOIInfo["fileId"]) => {
        await approveCOI(teamId, user!.id)
            .then(response => {
                // dispatch(
                //     alertActions.success({
                //         title: "COI Approved",
                //         dismissible: true,
                //     }),
                // );
                // Add this alert once the Alerts are a popover, and not an element that is scrolled to
                setCois(prev =>
                    prev?.map(coi =>
                        coi.fileId === response.coi.fileId ? response.coi : coi,
                    ),
                );
            })
            .catch(e => {
                dispatch(
                    alertActions.error({
                        title: "There was a problem approving the COI",
                        text: "If the problem persists, please contact a site admin.",
                        dismissible: true,
                    }),
                );
            });
    };

    const handleReject = async (
        fileId: ICOIInfo["fileId"],
        teamId: ICOIInfo["teamId"],
    ) => {
        await rejectCOI(teamId, message[fileId], user!.id)
            .then(response => {
                setCois(prev =>
                    prev?.map(coi =>
                        coi.fileId === response.coi.fileId ? response.coi : coi,
                    ),
                );
            })
            .catch(e => {
                dispatch(
                    alertActions.error({
                        title: "There was a problem rejecting the COI",
                        text: "If the problem persists, please contact a site admin.",
                        dismissible: true,
                    }),
                );
            })
            .finally(() => {
                handleMessageClose(fileId);
            });
    };

    const handleExpirationSave = async (
        fileId: ICOIInfo["fileId"],
        teamId: ICOIInfo["teamId"],
    ) => {
        await addCOIExpiration(teamId, expiration[fileId], user!.id)
            .then(response => {
                setCois(prev =>
                    prev?.map(coi =>
                        coi.fileId === response.coi.fileId ? response.coi : coi,
                    ),
                );
            })
            .catch(e => {
                dispatch(
                    alertActions.error({
                        title: "There was a problem adding a the expiration date to the COI",
                        text: "If the problem persists, please contact a site admin.",
                        dismissible: true,
                    }),
                );
            })
            .finally(() => {
                handleExpirationClose(fileId);
            });
    };

    const handleMessage = async (
        fileId: ICOIInfo["fileId"],
        teamId: ICOIInfo["teamId"],
    ) => {
        await addCOIMessage(teamId, message[fileId], user!.id)
            .then(response => {
                setCois(prev =>
                    prev?.map(coi =>
                        coi.fileId === response.coi.fileId ? response.coi : coi,
                    ),
                );
            })
            .catch(e => {
                dispatch(
                    alertActions.error({
                        title: "There was a problem adding a message to the COI",
                        text: "If the problem persists, please contact a site admin.",
                        dismissible: true,
                    }),
                );
            })
            .finally(() => {
                handleMessageClose(fileId);
            });
    };

    const handleMessageClose = (fileId: ICOIInfo["fileId"]) => {
        setShowMessage(prev => ({
            ...prev,
            [fileId]: false,
        }));
        setMessage(prev => {
            const updatedState = {
                ...prev,
            };
            delete updatedState[fileId];

            return updatedState;
        });
    };

    const handleExpirationClose = (fileId: ICOIInfo["fileId"]) => {
        setShowExpiration(prev => ({
            ...prev,
            [fileId]: false,
        }));
        setExpiration(prev => {
            const updatedState = {
                ...prev,
            };
            delete updatedState[fileId];

            return updatedState;
        });
    };

    const handleMessageChange = (
        e: ChangeEvent<{ value: string }>,
        fileId: ICOIInfo["fileId"],
    ) => {
        const { value } = e.target;
        setMessage(prev => ({ ...prev, [fileId]: value }));
    };

    const handleExpirationChange = (
        e: ChangeEvent<{ value: string }>,
        fileId: ICOIInfo["fileId"],
    ) => {
        const { value } = e.target;
        setExpiration(prev => ({ ...prev, [fileId]: value }));
    };

    return isOnApprovalPage ? (
        <section className="COIcontent" data-testid="COIcontent">
            <Row className="filterRow">
                <Col xs={12} sm={6} lg={4} xl={3} className="py-2">
                    <h4>Age Level:</h4>
                    <Select
                        isSearchable={false}
                        isClearable={true}
                        isMulti={true}
                        options={filters.ageLevels.options.map((a, idx) => {
                            return {
                                value: idx,
                                label: a,
                            };
                        })}
                        onChange={a => {
                            setFilters(prev => ({
                                ...prev,
                                ageLevels: {
                                    ...prev.ageLevels,
                                    filter:
                                        a.length === 0
                                            ? undefined
                                            : a?.map(o => o.label),
                                },
                            }));
                        }}
                    />
                </Col>
                <Col xs={12} sm={6} lg={4} xl={3} className="py-2">
                    <h4>League:</h4>
                    <Select
                        isSearchable={true}
                        isClearable={true}
                        isMulti={true}
                        options={filters.leagues.options.map((l, idx) => {
                            return {
                                value: idx,
                                label: l,
                            };
                        })}
                        onChange={l => {
                            setFilters(prev => ({
                                ...prev,
                                leagues: {
                                    ...prev.leagues,
                                    filter:
                                        l.length === 0
                                            ? undefined
                                            : l?.map(o => o.label),
                                },
                            }));
                        }}
                    />
                </Col>
                <Col xs={12} sm={6} lg={4} xl={3} className="py-2">
                    <h4>COI Status:</h4>
                    <Select
                        isSearchable={false}
                        isClearable={true}
                        isMulti={true}
                        options={filters.statuses.options.map((s, idx) => {
                            return {
                                value: idx,
                                label: s,
                            };
                        })}
                        defaultValue={{
                            value: filters.statuses.options.findIndex(o => {
                                return o === "Pending";
                            }),
                            label: "Pending",
                        }}
                        onChange={s => {
                            setFilters(prev => ({
                                ...prev,
                                statuses: {
                                    ...prev.statuses,
                                    filter:
                                        s.length === 0
                                            ? undefined
                                            : s?.map(o => o.label),
                                },
                            }));
                        }}
                    />
                </Col>
                <Col xs={12} sm={6} lg={4} xl={3} className="py-2">
                    {" "}
                    <h4>Uploaded Only</h4>
                    <Select
                        isSearchable={false}
                        isClearable={false}
                        options={[
                            {
                                value: 0,
                                label: "No",
                            },
                            {
                                value: 1,
                                label: "Yes",
                            },
                        ]}
                        defaultValue={{
                            value: 1,
                            label: "Yes",
                        }}
                        onChange={u => {
                            setFilters(prev => ({
                                ...prev,
                                uploadedOnly: {
                                    filter: u!.value,
                                },
                            }));
                        }}
                    />
                </Col>
                <Col xs={12} sm={6} lg={4} xl={3} className="py-2">
                    <h4>Team:</h4>
                    <Form.Control
                        type="text"
                        placeholder="Team"
                        onChange={e => {
                            setFilters(prev => ({
                                ...prev,
                                team:
                                    e.target.value.trim() === ""
                                        ? undefined
                                        : e.target.value.trim(),
                            }));
                        }}
                    />
                </Col>
                <Col xs={12} sm={6} lg={4} xl={3} className="py-2">
                    <h4>First Name:</h4>
                    <Form.Control
                        type="text"
                        placeholder="First Name"
                        onChange={e => {
                            setFilters(prev => ({
                                ...prev,
                                firstName:
                                    e.target.value.trim() === ""
                                        ? undefined
                                        : e.target.value.trim(),
                            }));
                        }}
                    />
                </Col>
                <Col xs={12} sm={6} lg={4} xl={3} className="py-2">
                    <h4>Last Name:</h4>
                    <Form.Control
                        type="text"
                        placeholder="Last Name"
                        onChange={e => {
                            setFilters(prev => ({
                                ...prev,
                                lastName:
                                    e.target.value.trim() === ""
                                        ? undefined
                                        : e.target.value.trim(),
                            }));
                        }}
                    />
                </Col>
                <Col xs={12} sm={6} lg={4} xl={3} className="py-2">
                    <h4>Email:</h4>
                    <Form.Control
                        type="text"
                        placeholder="Email"
                        onChange={e => {
                            setFilters(prev => ({
                                ...prev,
                                email:
                                    e.target.value.trim() === ""
                                        ? undefined
                                        : e.target.value.trim(),
                            }));
                        }}
                    />
                </Col>
            </Row>

            <Table className="coiTable" striped bordered responsive>
                <thead>
                    <tr>
                        <th>Team</th>
                        <th>League</th>
                        <th>COI Upload Date</th>
                        <th>COI Status</th>
                        <th>View</th>
                        <th>Expiration</th>
                        <th>Approval</th>
                    </tr>
                </thead>
                <tbody>
                    {cois
                        ?.filter(c =>
                            filters.team
                                ? c.teamName
                                      .toLowerCase()
                                      .includes(filters.team.toLowerCase())
                                : true,
                        )
                        .filter(c =>
                            filters.firstName
                                ? c.coach.firstName
                                      .toLowerCase()
                                      .includes(filters.firstName.toLowerCase())
                                : true,
                        )
                        .filter(c =>
                            filters.lastName
                                ? c.coach.lastName
                                      .toLowerCase()
                                      .includes(filters.lastName.toLowerCase())
                                : true,
                        )
                        .filter(c =>
                            filters.email
                                ? c.coach.email
                                      .toLowerCase()
                                      .includes(filters.email.toLowerCase())
                                : true,
                        )
                        .filter(c =>
                            filters.ageLevels.filter
                                ? filters.ageLevels.filter.includes(c.ageLevel)
                                : true,
                        )
                        .filter(c =>
                            filters.leagues.filter && c.league
                                ? filters.leagues.filter?.includes(c.league)
                                : true,
                        )
                        .filter(c =>
                            filters.statuses.filter
                                ? filters.statuses.filter?.includes(c.status)
                                : true,
                        )
                        .filter(c =>
                            filters.uploadedOnly.filter === 1 ? c.fileId : true,
                        )
                        .sort((a, b) => {
                            const nameCompare = a.teamName.localeCompare(
                                b.teamName,
                            );

                            if (nameCompare !== 0) {
                                return nameCompare;
                            }

                            const ageLevelComparison =
                                (a.ageLevel ? parseInt(a.ageLevel) : 0) -
                                (b.ageLevel ? parseInt(b.ageLevel) : 0);

                            if (ageLevelComparison !== 0) {
                                return ageLevelComparison;
                            }

                            return a.league && b.league
                                ? a.league.localeCompare(b.league)
                                : 0;
                        })
                        .map((c, idx) => (
                            <tr key={idx}>
                                <td>
                                    <b>{c.teamName}</b>
                                    <br />
                                    {c.coach.firstName} {c.coach.lastName} (
                                    {c.coach.email})
                                </td>
                                <td>{c.league}</td>
                                <td>
                                    {c.uploadDate &&
                                        defaultDateFormat.format(
                                            new Date(c.uploadDate),
                                        )}
                                </td>
                                <td>
                                    {c.status}
                                    {c.status && c.status !== "Pending" && (
                                        <>
                                            <br />
                                            {`${defaultDateFormat.format(
                                                new Date(c.approvalDate),
                                            )}`}
                                            <br />
                                            {c.approvedBy}
                                        </>
                                    )}
                                </td>
                                <td>
                                    {c.fileId && (
                                        <Link
                                            to={`${UPLOADED_COI_URL}${c.file}`}
                                            className="btn btn-primary"
                                            target="_blank"
                                        >
                                            View COI
                                        </Link>
                                    )}
                                </td>
                                <td>
                                    {c.expiration && (
                                        <>
                                            {defaultDateFormat.format(
                                                new Date(
                                                    `${c.expiration}T00:00:00`,
                                                ),
                                            )}
                                            <br />
                                        </>
                                    )}
                                    {!showExpiration[c.fileId] ? (
                                        c.expiration ? (
                                            <Button
                                                variant="primary"
                                                onClick={() =>
                                                    setShowExpiration(prev => ({
                                                        ...prev,
                                                        [c.fileId]: true,
                                                    }))
                                                }
                                            >
                                                Edit Expiration
                                            </Button>
                                        ) : (
                                            <Button
                                                variant="primary"
                                                onClick={() =>
                                                    setShowExpiration(prev => ({
                                                        ...prev,
                                                        [c.fileId]: true,
                                                    }))
                                                }
                                            >
                                                Set Expiration
                                            </Button>
                                        )
                                    ) : (
                                        <>
                                            <FormControl
                                                className="expiration"
                                                name="expiration"
                                                placeholder="Expiration Date"
                                                type="date"
                                                defaultValue={
                                                    c.expiration
                                                        ? c.expiration
                                                        : undefined
                                                }
                                                onChange={e =>
                                                    handleExpirationChange(
                                                        e,
                                                        c.fileId,
                                                    )
                                                }
                                            />
                                            <Button
                                                variant="warning"
                                                onClick={() =>
                                                    handleExpirationClose(
                                                        c.fileId,
                                                    )
                                                }
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                variant="success"
                                                onClick={() =>
                                                    handleExpirationSave(
                                                        c.fileId,
                                                        c.teamId,
                                                    )
                                                }
                                            >
                                                Save
                                            </Button>
                                        </>
                                    )}
                                </td>
                                <td>
                                    {c.status !== "Approved" && c.fileId && (
                                        <>
                                            <Button
                                                variant="primary"
                                                onClick={() =>
                                                    handleApproval(c.teamId)
                                                }
                                            >
                                                Approve
                                            </Button>
                                            {!showMessage[c.fileId] &&
                                                c.status !== "Rejected" && (
                                                    <Button
                                                        variant="danger"
                                                        onClick={() => {
                                                            setShowMessage(
                                                                prev => ({
                                                                    ...prev,
                                                                    [c.fileId]:
                                                                        true,
                                                                }),
                                                            );
                                                            setMessage(
                                                                prev => ({
                                                                    ...prev,
                                                                    [c.fileId]:
                                                                        DEFAULT_REJECT_MESSAGE,
                                                                }),
                                                            );
                                                        }}
                                                    >
                                                        Reject
                                                    </Button>
                                                )}
                                            {c.message &&
                                            c.message.trim() !== "" ? (
                                                c.status === "Rejected" ? (
                                                    <p>
                                                        <b>Rejected for:</b>{" "}
                                                        {c.message}
                                                    </p>
                                                ) : (
                                                    <p>
                                                        <b>Note:</b> {c.message}
                                                    </p>
                                                )
                                            ) : (
                                                ""
                                            )}
                                        </>
                                    )}
                                    {showMessage[c.fileId] ? (
                                        <>
                                            <FormControl
                                                as="textarea"
                                                className="message"
                                                name="message"
                                                placeholder="Message"
                                                value={message[c.fileId] ?? ""}
                                                onChange={e =>
                                                    handleMessageChange(
                                                        e,
                                                        c.fileId,
                                                    )
                                                }
                                            />
                                            <Button
                                                variant="warning"
                                                onClick={() =>
                                                    handleMessageClose(c.fileId)
                                                }
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                variant="success"
                                                onClick={() =>
                                                    handleMessage(
                                                        c.fileId,
                                                        c.teamId,
                                                    )
                                                }
                                            >
                                                Save Message
                                            </Button>
                                            <Button
                                                variant="danger"
                                                onClick={() =>
                                                    handleReject(
                                                        c.fileId,
                                                        c.teamId,
                                                    )
                                                }
                                            >
                                                Reject
                                            </Button>
                                        </>
                                    ) : (
                                        <Button
                                            variant="primary"
                                            onClick={() =>
                                                setShowMessage(prev => ({
                                                    ...prev,
                                                    [c.fileId]: true,
                                                }))
                                            }
                                        >
                                            Add Message
                                        </Button>
                                    )}
                                </td>
                            </tr>
                        ))}
                </tbody>
            </Table>
        </section>
    ) : (
        <Outlet />
    );
};

export default COIApproval;
